import React, { useState, useRef, useCallback } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

const PURPLE = '#6E43B9';
const GOAL_COLOR = '#FFC92A';
const CHART_LEFT_OFFSET = 5;   // approx px from wrapper left to plot area start
const CHART_RIGHT_OFFSET = 10;
const CHART_TOP_MARGIN = 16;
const CHART_XAXIS_HEIGHT = 25; // approx XAxis height in px
const SNAP_RADIUS = 18;        // px — how close mouse must be to a dot to trigger tooltip

function GoalLabel({ viewBox, value }) {
  if (!viewBox) return null;
  return (
    <text x={viewBox.x + 6} y={viewBox.y - 6} fontFamily="Inter" fontSize={11} fontWeight="500" fill={GOAL_COLOR}>
      {value}
    </text>
  );
}

function CustomDot({ cx, cy, index, dataLength }) {
  // index 0 is the phantom "0" point — skip it
  if (index === 0 || cy == null || isNaN(cy)) return null;
  return (
    <circle cx={cx} cy={cy} r={index === dataLength - 1 ? 5 : 4} fill={PURPLE} stroke="white" strokeWidth={2} />
  );
}


export default function ProgressGraph({ progressGraph }) {
  const [tooltip, setTooltip] = useState(null);
  const wrapperRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!wrapperRef.current || !progressGraph?.dataPoints?.length) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const dataPoints = progressGraph.dataPoints;

    const usableWidth = rect.width - CHART_LEFT_OFFSET - CHART_RIGHT_OFFSET;
    const plotHeight = 268 - CHART_TOP_MARGIN - CHART_XAXIS_HEIGHT;

    // Find the dot closest to the mouse and snap only if within SNAP_RADIUS.
    // chartData has a phantom "0" at index 0, so real points are at indices 1..n out of n total intervals.
    const n = dataPoints.length; // number of real points = number of intervals from "0" to last
    let closest = null;
    let closestDist = Infinity;
    dataPoints.forEach((point, i) => {
      const dotX = CHART_LEFT_OFFSET + ((i + 1) / n) * usableWidth;
      const dotY = CHART_TOP_MARGIN + (1 - point.percentage / 100) * plotHeight;
      const dist = Math.sqrt((mouseX - dotX) ** 2 + (mouseY - dotY) ** 2);
      if (dist < closestDist) { closestDist = dist; closest = { x: dotX, y: dotY, value: point.percentage }; }
    });

    if (closest && closestDist <= SNAP_RADIUS) {
      setTooltip(closest);
    } else {
      setTooltip(null);
    }
  }, [progressGraph]);

  if (!progressGraph) return null;
  const { hasData, dataPoints, goalLine } = progressGraph;

  // Prepend a phantom "0" origin point so the X axis always starts at 0
  const chartData = [{ date: '0', percentage: dataPoints.length === 1 ? 0 : null }, ...dataPoints];

  if (!hasData || dataPoints.length === 0) {
    return (
      <div className="bg-white rounded-[12px] p-[24px]" style={{ boxShadow: '0px 1px 8px 0px #14142B0A' }}>
        <h3 className="font-inter font-semibold text-[14px] text-[#45464E] mb-[12px]">Your Progress</h3>
        <div className="flex flex-col items-center justify-center text-center">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" className="mb-[10px] text-[#C4C9D4]">
            <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="font-inter text-[14px] text-[#45464EBF]">Take your first full mock to start tracking progress</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] p-[24px]" style={{ boxShadow: '0px 1px 8px 0px #14142B0A' }}>
      <h3 className="font-inter font-semibold text-[14px] text-[#45464E] mb-[4px]">Your Progress</h3>

      <div
        ref={wrapperRef}
        style={{ position: 'relative', cursor: 'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {tooltip && (
          <div style={{
            position: 'absolute',
            left: tooltip.x,
            top: Math.max(4, tooltip.y - 46),
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <div style={{
              background: '#493274',
              color: '#fff',
              borderRadius: '8px',
              padding: '3px 8.5px',
              fontSize: '12px',
              fontWeight: '500',
              fontFamily: 'Inter',
              whiteSpace: 'nowrap',
              lineHeight: '1.4',
            }}>
              {tooltip.value}%
            </div>
            <div style={{
              width: 0, height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #493274',
            }} />
          </div>
        )}

        <ResponsiveContainer width="100%" height={268}>
          <AreaChart data={chartData} margin={{ top: 16, right: 10, bottom: 0, left: -35 }}>
            <defs>
              <linearGradient id="progressFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="-13.33%" stopColor="#AF52DE" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#AF52DE" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid horizontal={true} vertical={false} strokeDasharray="4 4" stroke="#E5E7EB" />
            <XAxis dataKey="date" tick={{ fontFamily: 'Inter', fontSize: 11, fill: '#6C737F' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
            <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tick={{ fontFamily: 'Inter', fontSize: 11, fill: '#6C737F' }} tickLine={false} axisLine={false} />
            <ReferenceLine y={goalLine} stroke={GOAL_COLOR} strokeDasharray="5 4" strokeWidth={1.5} label={<GoalLabel value={`Goal ${goalLine}%`} />} />
            <Area
              type="monotone"
              dataKey="percentage"
              stroke={PURPLE}
              strokeWidth={2.5}
              fill="url(#progressFill)"
              dot={(props) => <CustomDot key={props.index} {...props} dataLength={chartData.length} />}
              activeDot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className="font-inter font-regular text-[12px] text-[#45464EBF] mt-[4px]">
        Based on your <strong>{dataPoints.length}</strong> most recent full mock {dataPoints.length === 1 ? 'exam' : 'exams'}.
      </p>
    </div>
  );
}
